<template>
  <div class="section-details-wrapper">
    <button
      class="close-button"
      @click="closeComponent"
      :title="$t('reusable.common.close')"
    >
      <b-icon-x-lg></b-icon-x-lg>
    </button>
    <!-- ************************************** Paso 1 ******************************************* -->
    <section v-if="step == 1">
      <div class="stepper-container">
        <div
          class="stepper-item active"
          :class="{ 'clickable-step': isEditing }"
          @click="goToStep(1)"
        >
          <div class="stepper-circle">
            <b-icon-list-ul></b-icon-list-ul>
          </div>
          <div class="stepper-label">
            {{
              $t("section.sectionView.detailsOf", {
                entity: $getVisibleNames("teaching.section", false, "Sección"),
              })
            }}
          </div>
        </div>
        <div class="stepper-line"></div>
        <div
          class="stepper-item"
          :class="{ 'clickable-step': isEditing }"
          @click="goToStep(2)"
        >
          <div class="stepper-circle">
            <b-icon-people-fill></b-icon-people-fill>
          </div>
          <div class="stepper-label">
            {{ $t("section.sectionView.assignStudents") }}
          </div>
        </div>
        <div class="stepper-line"></div>
        <div
          class="stepper-item"
          :class="{ 'clickable-step': isEditing }"
          @click="goToStep(3)"
        >
          <div class="stepper-circle">
            <b-icon-person-check-fill></b-icon-person-check-fill>
          </div>
          <div class="stepper-label">
            {{ $t("section.sectionView.assignTeachers") }}
          </div>
        </div>
      </div>
      <div class="container-group">
        <!-- Perfil de Egreso: solo cuando la vista padre no lo definió -->
        <div v-if="showEgressProfileSelector" class="group">
          <b-form-group
            :label="
              $getVisibleNames('mesh.egressprofile', false, 'Perfil de Egreso')
            "
            label-for="select-egress_profile"
            class="field-group"
          >
            <v-select
              id="select-egress_profile"
              v-model="current_egress_profile_id"
              :options="egress_profile_options"
              :reduce="(option) => option.id"
              :disabled="!allows_crud"
              :placeholder="
                $t('section.sectionView.selectEntity', {
                  entity: $getVisibleNames(
                    'mesh.egressprofile',
                    false,
                    'Perfil de Egreso'
                  ).toLowerCase(),
                })
              "
              @input="changeEgressProfile"
              label="label"
              track-by="id"
            >
              <template v-slot:no-options="{ search, searching }">
                <template v-if="searching">
                  {{ $t("section.sectionView.noSearchResults") }}
                  <em>{{ search }}</em
                  >.
                </template>
              </template>
            </v-select>
            <div
              class="div-invalid-feedback"
              v-if="
                !egressProfile && $v.section.egress_profile_matter.$anyError
              "
            >
              {{ $t("reusable.common.required") }}.
            </div>
            <div
              class="div-invalid-feedback"
              v-else-if="egressProfile && matterList.length == 0"
            >
              {{
                $t("section.sectionView.egressProfileWithoutSubjects", {
                  profile: $getVisibleNames(
                    "mesh.egressprofile",
                    false,
                    "Perfil de Egreso"
                  ),
                  subject: $getVisibleNames(
                    "mesh.egressprofilematter",
                    true,
                    "Asignaturas"
                  ),
                  section: $getVisibleNames(
                    "teaching.section",
                    false,
                    "Sección"
                  ),
                })
              }}
              <router-link
                :to="{
                  name: 'Matrix2View',
                  params: { egress_profile_id: egressProfile.id },
                }"
                class="link-modal-evaluations"
                >{{
                  $t("section.sectionView.goToMatrix2", {
                    matrix: $getVisibleNames(
                      "manual.matriz_2",
                      false,
                      "Matriz 2"
                    ),
                  })
                }}</router-link
              >.
            </div>
          </b-form-group>
        </div>
        <div class="group">
          <b-form-group
            :label="$t('reusable.common.name')"
            label-for="input-name"
            class="field-group"
          >
            <b-form-input
              id="input-name"
              v-model="$v.section.name.$model"
              :state="validateState('name')"
              :disabled="!allows_crud"
              aria-describedby="input-name-feedback"
              size="sm"
            ></b-form-input>
            <b-form-invalid-feedback id="input-name-feedback">
              <span v-if="section.name.trim() == ''">
                {{ $t("reusable.common.required") }}.
              </span>
              <span v-else>
                {{ $t("section.sectionView.sectionAlreadyExists") }}
              </span>
            </b-form-invalid-feedback>
          </b-form-group>
        </div>
        <div v-if="egressProfile" class="group">
          <b-form-group
            :label="
              $getVisibleNames('mesh.egressprofilematter', false, 'Asignatura')
            "
            label-for="select-egress_profile_matter"
            :description="matterLockedDescription"
            class="field-group"
          >
            <v-select
              id="select-egress_profile_matter"
              v-model="$v.section.egress_profile_matter.$model"
              :options="matterList"
              :reduce="(matterList) => matterList.id"
              :state="validateState('egress_profile_matter')"
              :disabled="!canChangeMatter"
              :placeholder="
                $t('section.sectionView.selectEntity', {
                  entity: $getVisibleNames(
                    'teaching.matter',
                    false,
                    'Asignatura'
                  ).toLowerCase(),
                })
              "
              @input="changeEgressProfileMatter"
              aria-describedby="select-egress_profile_matter-feedback"
              size="md"
              label="label"
              track-by="id"
            >
              <template v-slot:no-options="{ search, searching }">
                <template v-if="searching">
                  {{ $t("section.sectionView.noSearchResults") }}
                  <em>{{ search }}</em
                  >.
                </template>
              </template>
              <template v-slot:option="option">
                {{ option.label }}
              </template>
            </v-select>
            <div
              class="div-invalid-feedback"
              v-if="$v.section.egress_profile_matter.$anyError == true"
            >
              <div v-if="section.egress_profile_matter == null">
                {{ $t("reusable.common.required") }}.
              </div>
              <div
                v-if="
                  matter &&
                  matter.matter != null &&
                  theTestsHaveSomeErrors &&
                  isNaN(section.id)
                "
              >
                {{
                  $t(
                    "section.sectionView.cannotCreateSectionEvaluationErrors",
                    {
                      section: $getVisibleNames(
                        "teaching.section",
                        false,
                        "Sección"
                      ),
                      subject: $getVisibleNames(
                        "mesh.egressprofilematter",
                        false,
                        "Asignatura"
                      ),
                    }
                  )
                }}
                <div>
                  {{ $t("section.sectionView.forMoreInfo") }}
                  <span
                    class="link-modal-evaluations"
                    @click="$bvModal.show(`new-evaluation-list-modal`)"
                    >{{ $t("section.sectionView.seeMore") }}</span
                  >.
                </div>
              </div>
            </div>
          </b-form-group>
        </div>
        <div v-if="!egressProfile && !requiresEgressProfile" class="group">
          <b-form-group
            :label="$getVisibleNames('teaching.matter', false, 'Asignatura')"
            label-for="select-level"
            :description="matterLockedDescription"
            class="field-group"
          >
            <!-- :class="{ 'diable-search': section.matter != null }" -->
            <v-select
              v-model="$v.section.matter.$model"
              :options="sortMatters"
              :reduce="(sortMatters) => sortMatters.id"
              :state="validateState('matter')"
              :disabled="!canChangeMatter"
              :placeholder="
                $t('section.sectionView.selectEntityWithDashes', {
                  entity: $getVisibleNames(
                    'teaching.matter',
                    false,
                    'Asignatura'
                  ).toLowerCase(),
                })
              "
              label="label"
              track-by="id"
            >
              <template v-slot:no-options="{ search, searching }">
                <template v-if="searching">
                  {{ $t("section.sectionView.noSearchResults") }}
                  <em>{{ search }}</em
                  >.
                </template>
              </template>
              <template v-slot:option="option">
                {{ option.label }}
              </template>
            </v-select>
            <div
              class="div-invalid-feedback"
              v-if="$v.section.matter.$anyError == true"
            >
              {{ $t("reusable.common.required") }}.
            </div>
          </b-form-group>
        </div>
        <div class="group" v-if="!egressProfile && !requiresEgressProfile">
          <b-form-group
            :label="$getVisibleNames('mesh.periodtype', false, 'Régimen')"
            label-for="select-period_type"
            class="field-group"
          >
            <b-form-select
              id="select-period_type"
              :options="regimes"
              value-field="id"
              text-field="name"
              v-model="period_type_id"
              :disabled="!allows_crud || !edit_period"
              aria-describedby="select-period_type-feedback"
              @change="() => (section.period = null)"
              size="sm"
            ></b-form-select>
            <b-form-invalid-feedback id="select-period_type-feedback"
              >{{ $t("reusable.common.required") }}.</b-form-invalid-feedback
            >
          </b-form-group>
        </div>
        <!-- Jornada -->
        <div class="group">
          <b-form-group
            :label="$getVisibleNames('mesh.shift', false, 'Jornada')"
            label-for="select-shift"
            class="field-group"
          >
            <b-form-select
              id="select-shift"
              :options="shifts"
              value-field="id"
              text-field="name"
              v-model="$v.section.shift.$model"
              :state="validateState('shift')"
              :disabled="!allows_crud || !edit_shift"
              aria-describedby="select-shift-feedback"
              size="sm"
            ></b-form-select>
            <b-form-invalid-feedback id="select-shift-feedback"
              >{{ $t("reusable.common.required") }}.</b-form-invalid-feedback
            >
          </b-form-group>
        </div>
        <!-- Sede -->
        <div class="group">
          <b-form-group
            :label="$getVisibleNames('mesh.campus', false, 'Sede')"
            label-for="select-campus"
            class="field-group"
          >
            <b-form-select
              id="select-campus"
              :options="filteredCampuses"
              value-field="id"
              text-field="name"
              v-model="$v.section.campus.$model"
              :state="validateState('campus')"
              :disabled="!allows_crud || !edit_campus"
              aria-describedby="select-campus-feedback"
              size="sm"
            ></b-form-select>
            <b-form-invalid-feedback id="select-campus-feedback"
              >{{ $t("reusable.common.required") }}.</b-form-invalid-feedback
            >
            <!-- Sin Sedes en el Perfil de Egreso no se puede crear la Sección -->
            <div v-if="egressProfileWithoutCampuses" class="div-invalid-feedback">
              {{
                $t("section.sectionView.egressProfileWithoutCampuses", {
                  profile: $getVisibleNames(
                    "mesh.egressprofile",
                    false,
                    "Perfil de Egreso"
                  ),
                  campuses: $getVisibleNames("mesh.campus", true, "Sedes"),
                  section: $getVisibleNames(
                    "teaching.section",
                    false,
                    "Sección"
                  ),
                })
              }}
              <router-link
                :to="{
                  name: 'EgressProfileView',
                  params: { egress_profile_id: egressProfile.id },
                }"
                class="link-modal-evaluations"
                >{{
                  $t("section.sectionView.goToEgressProfile", {
                    profile: $getVisibleNames(
                      "mesh.egressprofile",
                      false,
                      "Perfil de Egreso"
                    ),
                  })
                }}</router-link
              >.
            </div>
          </b-form-group>
        </div>
        <!-- Periodo -->
        <div class="group">
          <b-form-group
            :label="$getVisibleNames('mesh.period', false, 'Período')"
            label-for="select-period"
            class="field-group"
          >
            <v-select
              id="select-period"
              v-model="$v.section.period.$model"
              :options="periodList"
              :reduce="(periodList) => periodList.id"
              :state="validateState('period')"
              :disabled="!allows_crud || !edit_period || periodList.length == 0"
              :placeholder="
                $t('section.sectionView.selectEntity', {
                  entity: $getVisibleNames(
                    'mesh.period',
                    false,
                    'Período'
                  ).toLowerCase(),
                })
              "
              aria-describedby="select-period-feedback"
              size="sm"
              label="label"
              track-by="id"
            >
              <template v-slot:no-options="{ search, searching }">
                <template v-if="searching">
                  {{ $t("section.sectionView.noSearchResults") }}
                  <em>{{ search }}</em
                  >.
                </template>
              </template>
              <template v-slot:option="option">
                {{ option.label }}
              </template>
            </v-select>
            <div
              class="div-invalid-feedback"
              id="select-period-feedback"
              v-if="$v.section.period.$anyError"
            >
              {{ $t("reusable.common.required") }}.
            </div>
          </b-form-group>
        </div>
      </div>
    </section>
    <!-- ************************************** Paso 2 ******************************************* -->
    <div class="hidden-step" :class="{ 'is-shown': this.step == 2 }">
      <div class="stepper-container">
        <div
          class="stepper-item completed"
          :class="{ 'clickable-step': isEditing }"
          @click="goToStep(1)"
        >
          <div class="stepper-circle">
            <b-icon-check2></b-icon-check2>
          </div>
          <div class="stepper-label">
            {{
              $t("section.sectionView.detailsOf", {
                entity: $getVisibleNames("teaching.section", false, "Sección"),
              })
            }}
          </div>
        </div>
        <div class="stepper-line completed"></div>
        <div
          class="stepper-item active"
          :class="{ 'clickable-step': isEditing }"
          @click="goToStep(2)"
        >
          <div class="stepper-circle">
            <b-icon-people-fill></b-icon-people-fill>
          </div>
          <div class="stepper-label">
            {{ $t("section.sectionView.assignStudents") }}
          </div>
        </div>
        <div class="stepper-line"></div>
        <div
          class="stepper-item"
          :class="{ 'clickable-step': isEditing }"
          @click="goToStep(3)"
        >
          <div class="stepper-circle">
            <b-icon-person-check-fill></b-icon-person-check-fill>
          </div>
          <div class="stepper-label">
            {{ $t("section.sectionView.assignTeachers") }}
          </div>
        </div>
      </div>
      <div>
        <h5 class="ml-3">{{ $t("section.sectionView.students") }}</h5>
        <SectionUserList
          v-if="step == 2"
          :key_component="'evaluatee:'"
          :users_ids="[].concat(section.students)"
          :section_id="section.id"
          :allows_crud="allows_crud"
          @changed="slotStudentsChanged"
          :required_group_name="'ESTUDIANTES'"
          :filter_by_student="true"
          :identifier_types="identifier_types"
          :roles="roles"
        ></SectionUserList>
      </div>
    </div>
    <!-- ************************************** Paso 3 ******************************************* -->
    <div class="hidden-step" :class="{ 'is-shown': this.step == 3 }">
      <div class="stepper-container">
        <div
          class="stepper-item completed"
          :class="{ 'clickable-step': isEditing }"
          @click="goToStep(1)"
        >
          <div class="stepper-circle">
            <b-icon-check2></b-icon-check2>
          </div>
          <div class="stepper-label">
            {{
              $t("section.sectionView.detailsOf", {
                entity: $getVisibleNames("teaching.section", false, "Sección"),
              })
            }}
          </div>
        </div>
        <div class="stepper-line completed"></div>
        <div
          class="stepper-item completed"
          :class="{ 'clickable-step': isEditing }"
          @click="goToStep(2)"
        >
          <div class="stepper-circle">
            <b-icon-check2></b-icon-check2>
          </div>
          <div class="stepper-label">
            {{ $t("section.sectionView.assignStudents") }}
          </div>
        </div>
        <div class="stepper-line completed"></div>
        <div
          class="stepper-item"
          :class="{
            active: !saved,
            completed: saved,
            'clickable-step': isEditing,
          }"
          @click="goToStep(3)"
        >
          <div class="stepper-circle">
            <b-icon-person-check-fill v-if="!saved"></b-icon-person-check-fill>
            <b-icon-check2 v-else></b-icon-check2>
          </div>
          <div class="stepper-label">
            {{ $t("section.sectionView.assignTeachers") }}
          </div>
        </div>
      </div>
      <div>
        <h5 class="ml-3">{{ $t("section.sectionView.teachers") }}</h5>
        <SectionUserList
          v-if="step == 3"
          :key_component="'evaluators:'"
          :section_id="section.id"
          :users_ids="[].concat(section.professors)"
          :required_group_name="'PROFESORES'"
          :allows_crud="allows_crud"
          @changed="slotProfessorsChanged"
          :filter_by_student="false"
          :identifier_types="identifier_types"
          :roles="roles"
        ></SectionUserList>
      </div>
    </div>
    <b-form-group
      v-if="step == 1"
      label-for="input-modification-date"
      label-cols="0"
      label-cols-sm="0"
      class="p-0 mt-2 mb-2 ml-1 text-left"
    >
      <ModificationDate :Information="section"></ModificationDate>
    </b-form-group>
    <div class="mb-4 mt-3 d-flex justify-content-between align-items-center">
      <ButtonMedium
        v-if="show_delete_button && !isNaN(section.id) && allows_crud"
        v-can="'teaching.delete_section'"
        :tooltip_text="''"
        :text_button="$t('reusable.common.delete')"
        :click_button="askForDelete"
        :icon="''"
        :variant="'danger'"
        :custom_class="'mr-1'"
      >
      </ButtonMedium>
      <!-- <b-button
          class="mr-1"
          style="margin-top: -9%"
          size="sm"
          variant="danger"
          v-can="'teaching.delete_section'"
          @click="askForDelete"
          >Eliminar</b-button
        > -->

      <!-- Botones de navegación a la derecha -->
      <div class="ml-auto">
        <ButtonMedium
          v-if="step != 1"
          :tooltip_text="''"
          :text_button="$t('section.sectionView.previousStep')"
          :click_button="prevStep"
          :icon="''"
          :variant="'primary'"
          :custom_class="'mr-1'"
        >
        </ButtonMedium>
        <!-- <b-button v-if="step != 1" class="mr-1" @click.prevent="prevStep">
        Paso anterior
      </b-button> -->
        <ButtonMedium
          v-if="step != total_steps"
          :tooltip_text="''"
          :text_button="$t('section.sectionView.nextStep')"
          :click_button="nextStep"
          :icon="''"
          :variant="'primary'"
          :custom_class="''"
        >
        </ButtonMedium>
        <!-- <b-button v-if="step != total_steps" @click.prevent="nextStep">
        Siguiente paso
      </b-button> -->

        <template v-if="step == 3 && allows_crud">
          <ButtonMedium
            v-if="!disabled_button_save"
            v-can="'teaching.add_section'"
            :tooltip_text="''"
            :text_button="$t('reusable.common.save')"
            :click_button="save"
            :icon="''"
            :variant="'primary'"
            :custom_class="'mr-1'"
          >
          </ButtonMedium>
          <ButtonMedium
            v-else
            v-can="'teaching.add_section'"
            :tooltip_text="``"
            :text_button="``"
            :icon="''"
            :variant="'primary'"
            :custom_class="`mr-1`"
            :disabled_button="true"
          >
          </ButtonMedium>
        </template>
        <!-- <template v-if="step == 3 && allows_crud">
        <b-button
          v-if="!disabled_button_save"
          class="mr-1"
          @click="save"
          v-can="'teaching.add_section'"
          >Guardar</b-button
        >
        <b-button v-else class="mr-1" disabled v-can="'teaching.add_section'"
          >Guardar</b-button
        >
      </template> -->
      </div>
    </div>
    <div style="text-align: right" v-if="step == 3"></div>
    <b-modal
      v-if="matter && matter.matter != null"
      :id="`new-evaluation-list-modal`"
      hide-footer
      size="xl"
      :title="$t('section.sectionView.incompleteInfo')"
    >
      <InfoEvaluations
        :matter_id="matter.matter"
        :egress_profile_matter_id="matter.id"
        @close_modal="$bvModal.hide(`new-evaluation-list-modal`)"
      ></InfoEvaluations>
    </b-modal>
  </div>
</template>

<script>
import { generateUniqueId, toast, showLoadingAlert } from "@/utils/utils";
import * as names from "@/store/names";
import { mapGetters, mapMutations } from "vuex";
import { validationMixin } from "vuelidate";
import vSelect from "vue-select";
import "vue-select/dist/vue-select.css";
import {
  required,
  // minValue,
  // maxValue,
  // numeric,
} from "vuelidate/lib/validators";

export default {
  name: "SectionDetails",
  mixins: [validationMixin],
  components: {
    vSelect,
    SectionUserList: () => import("./SectionUserList"),
    InfoEvaluations: () =>
      import("@/components/new-evaluations/Evaluation/InfoEvaluations.vue"),
    ModificationDate: () => import("@/components/reusable/ModificationDate"),
    ButtonMedium: () =>
      import("@/components/reusable/Buttons/Medium/ButtonMedium"),
  },
  props: {
    Section: {
      type: Object,
    },
    egress_profile_id: {},
    // Opciones { id, label } para elegir el Perfil de Egreso dentro del
    // formulario cuando la vista padre no tiene uno seleccionado. Si se
    // entregan, la Sección nueva debe quedar asociada a un Perfil de Egreso.
    egress_profile_options: {
      type: Array,
      default() {
        return [];
      },
    },
    selected_matter_id: {},
    period_id: {
      type: Number,
    },
    shift_id: {
      type: Number,
    },
    campus_id: {
      type: Number,
    },
    selected_level: {
      type: [Number, null],
      default: null,
    },
    period_type: {
      type: Number,
    },
    allows_crud: {
      type: Boolean,
      default: false,
    },
    show_delete_button: {
      type: Boolean,
      default: true,
    },
    edit_matter: {
      type: Boolean,
      default: true,
    },
    edit_level: {
      type: Boolean,
      default: true,
    },
    edit_period: {
      type: Boolean,
      default: true,
    },
    edit_shift: {
      type: Boolean,
      default: true,
    },
    edit_campus: {
      type: Boolean,
      default: true,
    },
    identifier_types: {
      type: Array,
      default() {
        return [];
      },
    },
    roles: {
      type: Array,
      default() {
        return [];
      },
    },
  },
  data() {
    return {
      step: 1,
      total_steps: 3,
      current_egress_profile_id: this.egress_profile_id,
      saved: false,
      matters_evaluations: [],
      disabled_button_save: false,
      period_type_id: this.Section ? null : this.period_type,
      section: {
        id: this.Section ? this.Section.id : generateUniqueId(),
        name: this.Section ? this.Section.name : "",
        color: this.Section ? this.Section.color : "#000000",
        attention_hours: this.Section ? this.Section.attention_hours : "",
        coexistence_rules: this.Section ? this.Section.coexistence_rules : "",
        // TODO: Preguntar si se mantiene fuera el plan_level de Section.
        // plan_level: this.Section ? this.Section.plan_level : 1,
        egress_profile_matter: this.Section
          ? this.Section.egress_profile_matter
          : null,
        matter: this.Section ? this.Section.matter : this.selected_matter_id,
        period: this.Section ? this.Section.period : this.period_id,
        shift: this.Section ? this.Section.shift : this.shift_id,
        campus: this.Section ? this.Section.campus : this.campus_id,
        students: this.Section ? this.Section.students : [],
        professors: this.Section ? this.Section.professors : [],
        sessions: this.Section ? this.Section.sessions : [],
        session_dates: this.Section ? this.Section.session_dates : [],
        updated_by: this.Section ? this.Section.updated_by : null,
        update_date: this.Section ? this.Section.update_date : null,
      },
    };
  },
  validations() {
    return {
      section: {
        name: {
          required,
          isUnique() {
            if (
              this.sections.some(
                (x) =>
                  x.id != this.section.id &&
                  this.$equals(x.name, this.section.name) &&
                  x.period === this.section.period &&
                  x.shift === this.section.shift &&
                  x.egress_profile_matter ==
                    this.section.egress_profile_matter &&
                  x.matter === this.section.matter &&
                  x.campus === this.section.campus
              )
            )
              return false;
            else return true;
          },
        },
        color: {},
        // plan_level: {
        //   required,
        //   numeric,
        //   minValue: minValue(1),
        //   maxValue: maxValue(18),
        // },
        matter: {
          valid(value) {
            if (!this.egressProfile && !this.requiresEgressProfile) {
              if (value) return true;
              else return false;
            } else return true;
          },
        },
        egress_profile_matter: {
          valid(value) {
            if (this.egressProfile) {
              if (value != null && this.matter && this.matter.matter != null) {
                if (!this.theTestsHaveSomeErrors && isNaN(this.section.id))
                  return true;
                else if (!isNaN(this.section.id)) return true;
                else return false;
              } else return false;
            }
            return !this.requiresEgressProfile;
          },
          // validMatter(value) {
          //   const matter = this.egress_profiles_matters.find(
          //     (x) => x.id == value
          //   );
          //   if (matter) {
          //     if (matter.is_closed) return true;
          //     else return false;
          //   } else return false;
          // },
        },
        period: { required },
        shift: { required },
        campus: { required },
        students: {},
        professors: {},
      },
    };
  },
  computed: {
    ...mapGetters({
      matters: names.MATTERS,
      sections: names.SECTIONS,
      egress_profiles_matters: names.EGRESS_PROFILES_MATTERS,
      egressProfiles: names.EGRESS_PROFILES,
      periods: names.PERIODS,
      shifts: names.SHIFTS,
      campuses: names.CAMPUSES,
      regimes: names.REGIMES,
      user: "getUser",
      institution: "getInstitution",
      evaluations: names.NEW_EVALUATIONS,
      tests: names.NEW_TESTS,
      evaluationCriterias: names.EVALUATION_CRITERIAS,
      studyUnits: names.STUDY_UNITS,
    }),
    isEditing() {
      return !isNaN(this.section.id);
    },
    egressProfile() {
      if (!this.current_egress_profile_id) return null;
      return this.egressProfiles.find(
        (x) => x.id == this.current_egress_profile_id
      );
    },
    // La Asignatura solo se define al crear: las evaluaciones y demás
    // componentes de la Sección se generan a partir de ella en ese momento.
    canChangeMatter() {
      return this.allows_crud && this.edit_matter && !this.isEditing;
    },
    matterLockedDescription() {
      if (!this.isEditing || !this.allows_crud) return null;
      return this.$t("section.sectionView.matterLockedAfterCreation", {
        subject: this.$getVisibleNames(
          "mesh.egressprofilematter",
          false,
          "Asignatura"
        ),
        section: this.$getVisibleNames("teaching.section", false, "Sección"),
      });
    },
    egressProfileWithoutCampuses() {
      return !!this.egressProfile && this.filteredCampuses.length == 0;
    },
    requiresEgressProfile() {
      return this.egress_profile_options.length > 0 && !this.isEditing;
    },
    showEgressProfileSelector() {
      return this.requiresEgressProfile && !this.egress_profile_id;
    },
    levels() {
      let list = [];
      if (!this.egressProfile) {
        for (let i = 1; i <= this.institution.max_semester_amount; i++) {
          list.push(i);
        }
        return list;
      } else {
        for (
          let index = 1;
          index <= this.egressProfile.semester_amount;
          index++
        ) {
          list.push(index);
        }
        return list;
      }
    },
    matterList() {
      let matterList = [];
      if (!this.current_egress_profile_id) return matterList;
      // La Asignatura de la Sección siempre se incluye, aunque no calce con
      // los filtros, para que el formulario de edición la muestre.
      let list = this.egress_profiles_matters.filter(
        (x) =>
          x.egress_profile == this.current_egress_profile_id ||
          x.id == this.section.egress_profile_matter
      );
      if (this.selected_level != null)
        list = list.filter(
          (x) =>
            x.plan_level == this.selected_level ||
            x.id == this.section.egress_profile_matter
        );
      if (list.length > 0) {
        matterList = list.map((x) => {
          const matter = this.matters.find((matter) => matter.id == x.matter);
          if (matter)
            return {
              ...x,
              name: matter.name,
              label:
                matter.code +
                (this.institution &&
                this.institution.use_epm_code &&
                x.code &&
                x.code !== matter.code
                  ? " - " + x.code
                  : "") +
                " - " +
                matter.name,
            };
        });
      }
      return matterList.filter((x) => x);
    },
    sortMatters() {
      return this.matters
        .map((x) => ({ ...x, label: x.code + " - " + x.name }))
        .sort((a, b) => {
          if (a.label < b.label) return -1;
          if (a.label > b.label) return 1;
          return 0;
        });
    },
    periodList() {
      let periods_list = this.periods.map((period) => {
        let start_date = period.start_date;
        let end_date = period.end_date;
        if (start_date)
          start_date = this.$moment(start_date).format("DD/MM/YYYY");
        if (end_date) end_date = this.$moment(end_date).format("DD/MM/YYYY");
        return {
          ...period,
          label: `${period.name} De: ${start_date} al ${end_date}`,
        };
      });
      if (!this.period_type_id) return periods_list;
      return periods_list
        .filter((x) => x.period_type == this.period_type_id)
        .sort(function (a, b) {
          if (a.start_date < b.start_date) return 1;
          if (a.start_date > b.start_date) return -1;
        });
    },
    filteredCampuses() {
      if (!this.egressProfile)
        return [...this.campuses].sort((a, b) => {
          if (a.name < b.name) return -1;
          if (a.name > b.name) return 1;
          return 0;
        });
      let list = [];
      this.egressProfile.campuses.forEach((campus_id) => {
        const instance = this.campuses.find((x) => x.id == campus_id);
        if (instance) list.push(instance);
      });
      return list.sort((a, b) => {
        if (a.name < b.name) return -1;
        if (a.name > b.name) return 1;
        return 0;
      });
    },
    matter() {
      if (this.egressProfile && this.section.egress_profile_matter != null)
        return this.matterList.find(
          (x) => x.id == this.section.egress_profile_matter
        );
      return null;
    },
    filterEvaluationsByMatter() {
      // scope==3 para base_matter y para duoc aunque sea del egressProfile la sección.
      if (this.matter)
        return this.evaluations.filter(
          (x) => x.scope == 3 && x.scope_id == this.matter.matter
        );
      return [];
    },
    filterTestsByMatter() {
      const evaluations_ids = this.filterEvaluationsByMatter.map((x) => x.id);
      return this.tests.filter((x) => evaluations_ids.includes(x.evaluation));
    },
    theTestsHaveSomeErrors() {
      if (
        this.institution &&
        this.institution.internal_use_id == "duoc.cl" &&
        this.institution.kl_assessment
      ) {
        if (
          this.filterTestsByMatter.some(
            (x) =>
              x.is_valid[0] == false ||
              (x.is_valid == true && x.manual_is_valid === false)
          ) ||
          this.filterTestsByMatter.length == 0 ||
          this.excludeECMicro.length > 0
        )
          return true;
      }
      return false;
    },
    excludeECMicro() {
      const evaluation_criterio_micro_ids = [
        ...new Set(
          this.matters_evaluations
            .map((x) => x.evaluation_criterias_micro)
            .flat()
        ),
      ];
      return this.ECMicroFilterByStudyUnit.filter(
        (x) => !evaluation_criterio_micro_ids.includes(x.id)
      );
    },
    ECMicroFilterByStudyUnit() {
      const study_unit_ids = this.studyUnitsFilterByMatter.map((x) => x.id);
      return this.evaluationCriterias
        .filter((x) => study_unit_ids.includes(x.study_unit))
        .sort(function (a, b) {
          if (a.order > b.order) return 1;
          if (a.order < b.order) return -1;
          return 0;
        });
    },
    studyUnitsFilterByMatter() {
      return this.studyUnits
        .filter((x) => x.matter == this.matter.matter)
        .sort(function (a, b) {
          if (a.order > b.order) return 1;
          if (a.order < b.order) return -1;
          return 0;
        });
    },
  },
  methods: {
    ...mapMutations([
      "setUserSectionProfessor",
      "UpdateUserSectionProfessor",
      "DeleteUserSectionProfessor",
    ]),

    closeComponent() {
      this.$emit("closed");
    },
    changeEgressProfile() {
      // La Asignatura y la Sede dependen del Perfil de Egreso elegido.
      this.section.egress_profile_matter = null;
      if (
        this.egressProfile &&
        !this.egressProfile.campuses.includes(this.section.campus)
      )
        this.section.campus = null;
      this.fetchEgressProfileMatters();
    },
    // Al editar, asegura que la Asignatura de la Sección esté en el store
    // aunque no venga en el listado cargado para el Perfil de Egreso.
    fetchSectionMatter() {
      if (!this.isEditing) return;
      if (this.section.egress_profile_matter) {
        const egress_profile_matter = this.egress_profiles_matters.find(
          (x) => x.id == this.section.egress_profile_matter
        );
        const request = egress_profile_matter
          ? Promise.resolve(egress_profile_matter)
          : this.$store.dispatch(
              names.FETCH_EGRESS_PROFILE_MATTER,
              this.section.egress_profile_matter
            );
        request.then((response) => {
          if (!this.current_egress_profile_id)
            this.current_egress_profile_id = response.egress_profile;
          this.$store.dispatch(names.FETCH_MATTER, response.matter);
        });
      } else if (this.section.matter) {
        this.$store.dispatch(names.FETCH_MATTER, this.section.matter);
      }
    },
    fetchEgressProfileMatters() {
      if (!this.current_egress_profile_id) return;
      this.$store.dispatch(names.FETCH_MATTERS, {
        egress_profile_id: this.current_egress_profile_id,
      });
      this.$store.dispatch(names.FETCH_EGRESS_PROFILES_MATTERS, {
        egress_profile_id: this.current_egress_profile_id,
      });
    },
    validateState(name) {
      const { $dirty, $error } = this.$v.section[name];
      return $dirty ? !$error : null;
    },
    save() {
      this.$v.section.$touch();
      if (this.$v.section.$anyError) {
        return;
      }
      this.disabled_button_save = true;
      // if (this.step == 3 && this.section.professors.length == 0) {
      //   return this.$swal(
      //     "No se han ingresado profesores",
      //     "Por favor, asegúrese de asignar profesores a la sección antes de guardar.",
      //     "warning"
      //   );
      // }
      if (isNaN(this.section.id)) this.create();
      else this.update();
      this.saved = true;
    },
    create() {
      showLoadingAlert(
        this.$t("section.sectionView.creating", {
          entity: this.$getVisibleNames("teaching.section", false, "Sección"),
        }),
        this.$t("section.sectionView.pleaseWaitWhileCreating")
      );
      this.$store
        .dispatch(names.POST_SECTION, this.section)
        .then((response) => {
          if (this.section.professors.find((x) => x == this.user.id)) {
            let payload = { user_id: this.user.id, section: response };
            this.setUserSectionProfessor(payload);
          }
          this.$restful
            .Get(
              `/teaching/fill_evaluation_users_default/?section=${response.id}`
            )
            .then(() => {
              this.$emit("created", response);
              this.$swal.close();
              this.disabled_button_save = false;
            })
            .catch(() => {
              this.$emit("created", response);
              this.$swal.close();
              this.disabled_button_save = false;
            });
          toast(
            this.$t("section.sectionView.entityCreated", {
              entity: this.$getVisibleNames(
                "teaching.section",
                false,
                "Sección"
              ),
            })
          );
        })
        .catch(() => {
          this.$emit("created");
          this.$swal.close();
          this.disabled_button_save = false;
        });
    },
    update() {
      this.$store
        .dispatch(names.UPDATE_SECTION, this.section)
        .then((response) => {
          let payload = { user_id: this.user.id, section: response };
          this.UpdateUserSectionProfessor(payload);
          this.$restful
            .Get(
              `/teaching/fill_evaluation_users_default/?section=${response.id}`
            )
            .then(() => {
              this.$emit("updated", response);
              this.disabled_button_save = false;
            })
            .catch(() => {
              this.$emit("updated", response);
              this.disabled_button_save = false;
            });
          toast(
            this.$t("section.sectionView.entityModified", {
              entity: this.$getVisibleNames(
                "teaching.section",
                false,
                "Sección"
              ),
            })
          );
        });
    },
    askForDelete() {
      this.$swal({
        title: this.$t("section.sectionView.confirmDeleteSection"),
        text: this.$t("reusable.common.deleteConfirmText"),
        type: "warning",
        showCancelButton: true,
      }).then((result) => {
        if (result.value) this.delete();
      });
    },
    delete() {
      this.$store.dispatch(names.DELETE_SECTION, this.section.id).then(() => {
        this.$emit("deleted", this.section);
        let payload = { user_id: this.user.id, section: this.section };
        this.DeleteUserSectionProfessor(payload);
        toast(
          this.$t("section.sectionView.entityDeleted", {
            entity: this.$getVisibleNames("teaching.section", false, "Sección"),
          })
        );
      });
    },

    slotStudentsChanged(students) {
      this.section.students = [];
      students.forEach((user) => {
        const index = this.section.students.findIndex((x) => x == user.id);
        if (index == -1) this.section.students.push(user.id);
      });
    },
    slotProfessorsChanged(professors) {
      this.section.professors = [];
      professors.forEach((user) => {
        const index = this.section.professors.findIndex((x) => x == user.id);
        if (index == -1) this.section.professors.push(user.id);
      });
    },
    goToStep(targetStep) {
      if (this.isEditing) {
        this.step = targetStep;
      }
    },
    prevStep() {
      return this.step--;
    },
    nextStep() {
      if (this.allows_crud) {
        this.$v.section.$touch();
        if (this.$v.section.$anyError) {
          return;
        }
      }
      // if (this.step == 2 && this.section.students.length == 0) {
      //   return this.$swal(
      //     "No se han ingresado estudiantes",
      //     "Por favor, asegúrese de asignar estudiantes a la sección antes de continuar.",
      //     "warning"
      //   );
      // }
      return this.step++;
    },
    fetchInitEvaluations() {
      if (
        this.institution &&
        this.institution.internal_use_id == "duoc.cl" &&
        this.institution.kl_assessment
      ) {
        // scoped_id:3 para base_matter
        if (this.matter)
          this.$store
            .dispatch(names.FETCH_NEW_EVALUATIONS, {
              scoped_id: 3,
              scoped_object_id: this.matter.matter,
            })
            .then((evaluations) => {
              this.$store.dispatch(names.FETCH_NEW_TESTS, {
                evaluations_ids: evaluations.map((x) => x.id),
              });
            });
      }
    },
    fetchECM() {
      if (this.matter) {
        this.$store.dispatch(names.FETCH_STUDY_UNITS, this.matter.matter);
        this.$store.dispatch(names.FETCH_EVALUATION_CRITERIAS, {
          matter_id: this.matter.matter,
        });
      }
    },
    changeEgressProfileMatter(value) {
      if (
        value != null &&
        this.egressProfile &&
        this.institution &&
        this.institution.internal_use_id == "duoc.cl" &&
        this.institution.kl_assessment
      ) {
        this.fetchInitEvaluations();
        this.fetchECM();
        this.fetchMattersEvaluation();
      }
    },
    fetchMattersEvaluation() {
      if (this.matter && this.matter.matter)
        this.$restful
          .GetAll(`/teaching/matter-evaluation/?matter=${this.matter.matter}`)
          .then((response) => {
            this.matters_evaluations = response.sort(function (a, b) {
              if (a.order > b.order) return 1;
              if (a.order < b.order) return -1;
              return 0;
            });
          });
    },
  },
  watch: {
    egress_profile_id(value) {
      this.current_egress_profile_id = value;
      this.fetchEgressProfileMatters();
    },
  },
  created() {
    if (!isNaN(this.section.id)) {
      const period = this.periods.find((x) => x.id == this.section.period);
      if (period) this.period_type_id = period.period_type;
    }
    this.saved = false;
    this.$store.dispatch(names.FETCH_SHIFTS);
    this.fetchEgressProfileMatters();
    this.fetchSectionMatter();
  },
};
</script>

<style scoped>
.section-details-wrapper {
  position: relative;
  padding: 1rem;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  background-color: #fff;
  margin-bottom: 2rem;
}

.close-button {
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: none;
  border: none;
  font-size: 1rem;
  color: #6c757d;
  cursor: pointer;
  padding: 0.25rem;
  width: 25px;
  height: 25px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: all 0.2s ease;
  z-index: 10;
}

.close-button:hover {
  background-color: #f8f9fa;
  color: #dc3545;
}

p {
  margin-bottom: 0px !important;
  margin-top: 9px !important;
}
.div-invalid-feedback {
  display: block;
  width: 100%;
  margin-top: 0.25rem;
  font-size: 0.875em;
  color: #dc3545;
}
.group {
  width: 100%;
}
.hidden-step {
  display: none;
}
.is-shown {
  display: block;
}
/* Grid de una columna: campos alineados verticalmente, con separación fija
   entre ellos para que no queden pegados */
.container-group {
  display: grid;
  grid-template-columns: 1fr;
  row-gap: 1.5rem;
  margin-top: 2rem;
  margin-bottom: 0.5rem;
  text-align: left;
}
.container-group >>> .field-group {
  margin: 0;
}
.container-group >>> .field-group label {
  font-size: var(--kl-fs-small, 0.8125rem);
  font-weight: var(--kl-fw-semibold, 600);
  color: var(--kl-text-heading, #000);
  margin-bottom: 0.4rem;
}
.container-group >>> .field-group .custom-select,
.container-group >>> .field-group input.form-control {
  height: var(--kl-input-height, 2.25rem);
  font-size: var(--kl-input-fs, var(--kl-fs-body, 0.875rem));
  border: 1px solid var(--kl-input-border, #c7d1dd);
  border-radius: var(--kl-input-radius, 8px);
}
.container-group >>> .field-group .custom-select:focus,
.container-group >>> .field-group input.form-control:focus {
  border-color: var(--kl-input-border-focus, #007bff);
  box-shadow: var(--kl-shadow-focus, 0 0 0 0.18rem rgba(0, 110, 200, 0.15));
}
.container-group >>> .field-group .vs__dropdown-toggle {
  min-height: var(--kl-input-height, 2.25rem);
  border: 1px solid var(--kl-input-border, #c7d1dd);
  border-radius: var(--kl-input-radius, 8px);
}
.link-modal-evaluations {
  text-decoration: underline;
  cursor: pointer;
  color: #0000ee;
}
.link-modal-evaluations:hover {
  font-weight: bold;
  color: #551a8b;
}

/* Nuevo diseño del stepper */
.stepper-container {
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 2rem 0 3rem 0;
  padding: 0 2rem;
}

.stepper-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  flex: 0 0 auto;
}

.stepper-circle {
  width: 50px;
  height: 50px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #e9ecef;
  border: 3px solid #dee2e6;
  color: #6c757d;
  font-size: 20px;
  transition: all 0.3s ease;
  position: relative;
  z-index: 2;
}

.stepper-item.active .stepper-circle {
  background-color: #007bff;
  border-color: #007bff;
  color: white;
  box-shadow: 0 0 0 4px rgba(0, 123, 255, 0.2);
  transform: scale(1.05);
}

.stepper-item.completed .stepper-circle {
  background-color: #28a745;
  border-color: #28a745;
  color: white;
}

.stepper-label {
  margin-top: 0.75rem;
  font-size: 0.875rem;
  text-align: center;
  color: #6c757d;
  max-width: 150px;
  font-weight: 500;
  transition: color 0.3s ease;
}

.stepper-item.active .stepper-label {
  color: #007bff;
  font-weight: 700;
}

.stepper-item.completed .stepper-label {
  color: #28a745;
}

.stepper-line {
  height: 3px;
  background-color: #dee2e6;
  flex: 1;
  margin: 0 1rem;
  max-width: 150px;
  position: relative;
  top: -25px;
  transition: background-color 0.3s ease;
}

.stepper-line.completed {
  background-color: #28a745;
}

.stepper-item.clickable-step {
  cursor: pointer;
}

.stepper-item.clickable-step .stepper-circle:hover {
  opacity: 0.85;
  transform: scale(1.1);
}

.stepper-item.clickable-step.active .stepper-circle:hover {
  transform: scale(1.15);
}

@media (max-width: 991px) {
  .stepper-container {
    padding: 0 1rem;
  }

  .stepper-circle {
    width: 45px;
    height: 45px;
    font-size: 18px;
  }

  .stepper-label {
    font-size: 0.75rem;
    max-width: 120px;
  }

  .stepper-line {
    margin: 0 0.5rem;
    max-width: 100px;
    top: -22px;
  }
}

@media (max-width: 768px) {
  .stepper-container {
    padding: 0 0.5rem;
  }

  .stepper-circle {
    width: 40px;
    height: 40px;
    font-size: 16px;
  }

  .stepper-label {
    font-size: 0.7rem;
    max-width: 100px;
  }

  .stepper-line {
    margin: 0 0.25rem;
    max-width: 60px;
    top: -20px;
  }
}
</style>